import { useEffect, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { authorizedFetch } from '@/lib/api'
import { getHrefWithThreadPreservation } from '@/utils/thread-navigation'
import { API_ORIGIN, getListPagination } from '../constants'
import { appendListPageParams } from '@/lib/list-pagination'
import { useAuth } from './use-auth'
import type { Opportunity } from './use-opportunities'
import type { ShortlistInsight } from '@/lib/shortlist-workbench'
import { FIT_POLL_MS, isInsightNewerThan } from '@/lib/analysis-wait'
import { useFitRetryPending } from '@/hooks/use-fit-retry-pending'

export const SAVED_OPPORTUNITIES_QUERY_KEY = ['saved-opportunities'] as const

export type SavedOpportunity = Opportunity & {
  is_available: boolean
  interest_at: string | null
  insight: ShortlistInsight | null
  insight_pending: boolean
}

export interface SavedOpportunitiesResponse {
  results: SavedOpportunity[]
  saved_at_by_opportunity_id: Record<string, string>
  page?: number
  page_size?: number
  total?: number
  has_next?: boolean
}

export function useSavedOpportunities(
  options: { enabled?: boolean; page?: number; pageSize?: number } = {},
) {
  const { enabled = true, page, pageSize } = options
  const listPagination = getListPagination('candidateShortlist')
  const paginateList = listPagination.enabled && page != null
  const resolvedPageSize = pageSize ?? listPagination.pageSize
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {
    opportunityStartedAt,
    waitingOpportunityIds,
    pendingOpportunityIds: retrySpinningIds,
    stuckOpportunityIds: retryStuckIds,
    markOpportunityPending,
    ensureOpportunityPending,
    clearOpportunityPending,
  } = useFitRetryPending()

  const savedQuery = useQuery({
    queryKey: paginateList
      ? [...SAVED_OPPORTUNITIES_QUERY_KEY, 'page', page, resolvedPageSize]
      : SAVED_OPPORTUNITIES_QUERY_KEY,
    queryFn: async (): Promise<SavedOpportunitiesResponse> => {
      const params = new URLSearchParams()
      if (paginateList) {
        appendListPageParams(params, page, resolvedPageSize)
      }
      const qs = params.toString()
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/opportunities/saved/${qs ? `?${qs}` : ''}`,
      )
      if (!response.ok) {
        throw new Error('Failed to fetch saved opportunities')
      }
      return response.json()
    },
    enabled: enabled && !!token,
    // Fit notes change after analyse/reanalyse — keep short so cards don't stick on old copy.
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const serverPending = query.state.data?.results?.some((row) => row.insight_pending)
      if (serverPending || waitingOpportunityIds.size > 0) return FIT_POLL_MS
      return false
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (opportunityId: string) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/opportunities/saved/`, {
        method: 'POST',
        body: JSON.stringify({ opportunity_id: opportunityId }),
      })
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error((error as { error?: string }).error || 'Failed to save opportunity')
      }
      return response.json()
    },
    onSuccess: (_data, opportunityId) => {
      const savedAt = new Date().toISOString()
      markOpportunityPending(opportunityId)
      queryClient.setQueryData<SavedOpportunitiesResponse>(
        SAVED_OPPORTUNITIES_QUERY_KEY,
        (prev) => {
          const base = prev ?? { results: [], saved_at_by_opportunity_id: {} }
          const results = base.results.map((row) =>
            row.id === opportunityId && !row.insight
              ? { ...row, insight_pending: true }
              : row,
          )
          return {
            results,
            saved_at_by_opportunity_id: {
              ...base.saved_at_by_opportunity_id,
              [opportunityId]:
                base.saved_at_by_opportunity_id[opportunityId] ?? savedAt,
            },
          }
        },
      )
      void queryClient.invalidateQueries({ queryKey: SAVED_OPPORTUNITIES_QUERY_KEY })
      toast.success('Saved — checking your fit…', {
        action: {
          label: 'View saved',
          onClick: () =>
            navigate(getHrefWithThreadPreservation('/candidate/jobs?tab=saved')),
        },
      })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not save this job')
    },
  })

  const unsaveMutation = useMutation({
    mutationFn: async (opportunityId: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/opportunities/saved/${opportunityId}`,
        { method: 'DELETE' },
      )
      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error((error as { error?: string }).error || 'Failed to remove saved opportunity')
      }
      return response.json()
    },
    onSuccess: (_data, opportunityId) => {
      clearOpportunityPending(opportunityId)
      queryClient.invalidateQueries({ queryKey: SAVED_OPPORTUNITIES_QUERY_KEY })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not remove this saved job')
    },
  })

  const savedAtByOpportunityId = useMemo(
    () => savedQuery.data?.saved_at_by_opportunity_id ?? {},
    [savedQuery.data?.saved_at_by_opportunity_id],
  )
  const savedOpportunities = useMemo(
    () => savedQuery.data?.results ?? [],
    [savedQuery.data?.results],
  )

  const savedIds = useMemo(
    () => new Set(Object.keys(savedAtByOpportunityId)),
    [savedAtByOpportunityId],
  )

  useEffect(() => {
    for (const row of savedOpportunities) {
      if (row.insight_pending && opportunityStartedAt[row.id] == null) {
        ensureOpportunityPending(row.id)
      }
    }
  }, [ensureOpportunityPending, opportunityStartedAt, savedOpportunities])

  useEffect(() => {
    for (const id of waitingOpportunityIds) {
      if (!savedIds.has(id)) {
        clearOpportunityPending(id)
        continue
      }
      const row = savedOpportunities.find((item) => item.id === id)
      if (isInsightNewerThan(row?.insight, opportunityStartedAt[id])) {
        clearOpportunityPending(id)
      }
    }
  }, [
    clearOpportunityPending,
    opportunityStartedAt,
    waitingOpportunityIds,
    savedIds,
    savedOpportunities,
  ])

  const pendingOpportunityIds = useMemo(() => {
    const ids = new Set(retrySpinningIds)
    for (const row of savedOpportunities) {
      if (row.insight_pending && !retryStuckIds.has(row.id)) ids.add(row.id)
      if (isInsightNewerThan(row.insight, opportunityStartedAt[row.id]) && !row.insight_pending) {
        ids.delete(row.id)
      }
    }
    for (const id of [...ids]) {
      if (!savedIds.has(id)) ids.delete(id)
    }
    return ids
  }, [opportunityStartedAt, retrySpinningIds, retryStuckIds, savedIds, savedOpportunities])

  const stuckOpportunityIds = useMemo(() => {
    const stuck = new Set<string>()
    for (const id of retryStuckIds) {
      if (savedIds.has(id)) stuck.add(id)
    }
    return stuck
  }, [retryStuckIds, savedIds])

  const toggleSave = (opportunityId: string, currentlySaved: boolean) => {
    if (currentlySaved) {
      unsaveMutation.mutate(opportunityId)
    } else {
      saveMutation.mutate(opportunityId)
    }
  }

  const savingOpportunityId =
    saveMutation.isPending && saveMutation.variables
      ? saveMutation.variables
      : unsaveMutation.isPending && unsaveMutation.variables
        ? unsaveMutation.variables
        : null

  return {
    savedOpportunities,
    savedAtByOpportunityId,
    savedCount: paginateList
      ? (savedQuery.data?.total ?? savedOpportunities.length)
      : Object.keys(savedAtByOpportunityId).length,
    pagination: paginateList
      ? {
          page: savedQuery.data?.page ?? page ?? 1,
          page_size: savedQuery.data?.page_size ?? resolvedPageSize,
          total: savedQuery.data?.total ?? 0,
          has_next: savedQuery.data?.has_next ?? false,
        }
      : undefined,
    savedIds,
    pendingOpportunityIds,
    stuckOpportunityIds,
    isLoading: savedQuery.isLoading,
    error: savedQuery.error,
    toggleSave,
    isSaving: saveMutation.isPending || unsaveMutation.isPending,
    savingOpportunityId,
    refetch: savedQuery.refetch,
  }
}
